import { ChangeEvent, FormEvent, FunctionComponent, useState } from 'react';

import { submit, SubmitResponse } from 'requests/contacts';

import Button from 'components/layout/Button';
import { ButtonLayouts, ButtonSizes, ButtonStyles, ButtonTypes, ButtonVariants } from 'components/layout/Button/types';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import Textarea from 'components/layout/forms/Textarea';
import Spinner from 'components/layout/Spinner';

import StyledComponent from './styles';
import { Props } from './types';

export interface FormState {
    fullName: string;
    email: string;
    phone: string;
    message: string;
}

const ContentFormsGeneralContact: FunctionComponent<Props> = ({ partnerId, translations, setFormSubmitted }) => {
    const [formState, setFormState] = useState<FormState>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formErrors, setFormErrors] = useState<FormState>(null);

    const onSubmit = (event: FormEvent<HTMLFormElement>): Promise<SubmitResponse | void> => {
        event.preventDefault();
        setFormErrors(null);
        setIsLoading(true);

        return submit({
            ...formState,
            partnerId: partnerId,
        })
            .then(() => {
                setFormState(null);
                setFormSubmitted(true);
            })
            .catch((error) => {
                console.error(error);
                setFormErrors(error?.payload?.validationErrors);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <StyledComponent className="content-forms-general-contact">
            <div className="contact-form-body">
                <header className="contact-form-header">
                    <p className="contact-header-headline">
                        {translations.contactFormHeaderOne}
                        <span className="highlighted">
                            {translations.contactFormHeaderTwo}
                        </span>
                    </p>
                    <p className="contact-description">
                        {translations.contactFormDescription}
                    </p>
                </header>
                {isLoading && (<Spinner />)}
                <Form
                    onSubmit={onSubmit}
                    className="form"
                >
                    <Input
                        name="fullName"
                        value={formState?.fullName}
                        error={formErrors?.fullName}
                        label={translations.inputsNameLabel}
                        placeholder={translations.inputsNameLabel}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setFormState({
                            ...formState,
                            fullName: event.target.value,
                        })}
                    />
                    <Input
                        name="email"
                        value={formState?.email}
                        error={formErrors?.email}
                        label={translations.inputsEmailLabel}
                        placeholder={translations.inputsEmailPlaceholder}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setFormState({
                            ...formState,
                            email: event.target.value,
                        })}
                    />
                    <Input
                        name="phone"
                        value={formState?.phone}
                        error={formErrors?.phone}
                        label={translations.inputsPhoneLabel}
                        placeholder={translations.inputsPhoneLabel}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setFormState({
                            ...formState,
                            phone: event.target.value,
                        })}
                    />

                    <Textarea
                        name="message"
                        value={formState?.message}
                        error={formErrors?.message}
                        label={translations.inputsMessageLabel}
                        placeholder={translations.inputsMessagePlaceholder}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setFormState({
                            ...formState,
                            message: event.target.value,
                        })}
                    />

                    <Button
                        className="gradient-180"
                        style={ButtonStyles.Primary}
                        variant={ButtonVariants.Primary}
                        layout={ButtonLayouts.Block}
                        size={ButtonSizes.Medium}
                        type={ButtonTypes.Submit}
                    >
                        {translations.submitButton}
                    </Button>
                </Form>
            </div>
        </StyledComponent>
    );
};

export default ContentFormsGeneralContact;
